import React, { useState, useEffect, useMemo } from 'react'
import { withResizeDetector } from 'react-resize-detector'
import { Box, LinearProgress, useTheme, Tooltip, CircularProgress } from "@material-ui/core"
import { Description } from "@material-ui/icons"
import { Text, Button, Image } from '.'
import { downloadFile } from 'shared/utils'
import clsx from 'clsx'
import useStyles from './styles'

/** lightbox for images */
import LightBox from 'yet-another-react-lightbox'
import Zoom from 'yet-another-react-lightbox/dist/plugins/zoom'
import Thumbnails from 'yet-another-react-lightbox/dist/plugins/thumbnails'
import Download from 'yet-another-react-lightbox/dist/plugins/download'
import Video from 'yet-another-react-lightbox/dist/plugins/video'
import 'yet-another-react-lightbox/dist/styles.css'
import 'yet-another-react-lightbox/dist/plugins/thumbnails/thumbnails.css'

const FilePreview = ({ value, media, allowDelete, onDelete, index }) => {
  const classes = useStyles()

  const [image, setImage] = useState(null)

  useEffect(() => {
    let reader = new FileReader();
    reader.onload = (e) => {
      setImage(e.target.result)
    };
    reader.readAsDataURL(media);
  }, [media])

  const del = (e) => {
    e.stopPropagation()
    let list = [...value]
    list.splice(index, 1)
    onDelete(list)
  }

  return (
    <div className={classes.imageInputImage} key={`preview-${index}`}>
      {allowDelete &&
        <div className={classes.imageInputDelete}>
          <Button kind="delete" variant="icon" size="small" onClick={del} />
        </div>
      }
      <img className={classes.imageFitHeight} src={image} alt={`preview-${index}-inner`} />
    </div>
  )
}

const FileListViewInner = (props) => {
  const {
    onDelete,
    value,
    width,
    height,
    useFileReader = false,
    useSmallThumbs = false,
    allowDelete,
    isUploading = false,
    onCancel = null,
    percentageProps = 0,
    className,
    textVariantNoAttachment = "body1",
    disableBorder = false
  } = props
  const classes = useStyles()
  const theme = useTheme()
  const imageExt = ['jpg', 'jpeg', 'png', 'bmp', 'gif', 'tiff', 'jfif', 'webp']
  const audioExt = ['mp3', 'wav', 'ogg', 'ogm']
  const videoExt = ['mp4', 'webm']

  /** lightbox states */
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxSrc, setLightboxSrc] = useState([])
  const [lightboxIdx, setLightboxIdx] = useState(0)
  const [isDownloading, setIsDownloading] = useState(false)

  const filteredValue = useMemo(() => {    
    if (Array.isArray(value)) {
      return value.filter(f => f?.image_url)
    } else if (value?.image_url) {
      return [value]
    }
  }, [value])

  useEffect(() => {
    if ((filteredValue && filteredValue.length > 0)) {
      const sourceSet = filteredValue
        .filter((f) => {
          const ext = f?.image_url.split('.').pop()?.toLowerCase()
          return imageExt.includes(ext) || videoExt.includes(ext)
        })
        .map((data) => {
          const ext = data?.image_url.split('.').pop()?.toLowerCase()
          if (imageExt.includes(ext)) {
            return {
              src: data?.image_url,
              uid: data?.uid
            }
          } else if (videoExt.includes(ext)) {
            return {
              type: "video",
              uid: data?.uid,
              sources: [
                {
                  src: data?.image_url,
                  type: `video/${ext}`,
                },
              ]
            }
          }
        })
      setLightboxSrc(sourceSet)
    }
  }, [filteredValue])

  const del = (e, i) => {
    e.preventDefault()
    let list = [...filteredValue]
    list.splice(i, 1)
    onDelete(list)
  }

  const handleAttachmentClick = async (e, media) => {
    e.preventDefault()
    e.stopPropagation()

    const ext = media?.image_url.split('.').pop()?.toLowerCase()

    if (imageExt.includes(ext) || videoExt.includes(ext)) {
      const idx = lightboxSrc?.findIndex(f => f?.uid === media?.uid)
      setLightboxIdx(idx)
      setLightboxOpen(true)
      return
    }

    downloadFile(
      media.image_url,
      media.file_name,
      () => setIsDownloading(true),
      () => setIsDownloading(false)
    )
  }

  if ((filteredValue && filteredValue.length > 0) || isUploading)
    return (
      <div className={clsx(classes.imageListInput, useSmallThumbs && classes.imageListInputSmall, className)} >
        <div
          className={classes.imageListInputContent}
          style={{
            // position: "absolute",
            width: "auto",
            height: `${height}px`,
            overflowX: "auto",
            overflowY: "hidden",
          }}
        >
          {filteredValue && filteredValue.map((media, index) =>
            <React.Fragment key={`media-wrapper-${index}`}>
              {useFileReader
                ? <FilePreview {...props} {...{ media }} />
                : <div
                  role="link"
                  tabIndex={0}
                  onClick={e => handleAttachmentClick(e, media)}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleAttachmentClick(e, media)}
                  style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
                >
                  <div className={useSmallThumbs ? classes.imageListInputSmall : classes.imageInput}>
                    {media?.image_url &&
                      <>
                        {imageExt.includes(media?.image_url.split('.').pop().toLowerCase())
                          ? <div className={clsx(classes.imageInputImage, !disableBorder && classes.imageInputImageBorder)}>
                            {allowDelete &&
                              <div className={classes.imageInputDelete}>
                                <Button
                                  kind="delete"
                                  variant="icon"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    del(e, index)
                                  }}
                                />
                              </div>
                            }
                            <img
                              className={useSmallThumbs ? classes.imageFitHeightSm : classes.imageFitHeight}
                              src={media?.image_url}
                              alt={media?.file_name}
                            />
                          </div>
                          : <Tooltip title={media?.file_name} arrow>
                            <div className={useSmallThumbs ? classes.imageInputPlaceholderSmall : classes.imageInputPlaceholder}>
                              <label htmlFor={'perform_upload'}>
                                {allowDelete &&
                                  <div className={classes.imageInputDelete}>
                                    <Button
                                      kind="delete"
                                      variant="icon"
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        del(e, index)
                                      }}
                                    />
                                  </div>
                                }
                                <Box display='flex' flexDirection='column' alignItems='center'>
                                  {isDownloading ? <CircularProgress size='1em' /> : <Description />}
                                  {/* <Text variant="caption">
                                    {media?.file_name}
                                  </Text> */}
                                  <div className={useSmallThumbs ? classes.filenameTextSmall : classes.filenameText}>
                                    {isDownloading ? 'Downloading' : media?.file_name}
                                  </div>
                                </Box>
                              </label>
                            </div>
                          </Tooltip>
                        }
                      </>
                    }
                  </div>
                </div>
              }
            </React.Fragment>
          ).reduce((p, n, i) => p ?
            [...p, <div key={`spacer-${i}`} className={clsx(filteredValue.length > 1 && classes.imageListInputSpacing)} />, n] : [n]
            , null
          )}
          {filteredValue &&
            <div className={clsx(filteredValue.length > 1 && classes.imageListInputSpacing)} />
          }
          {isUploading &&
            <>
              <div className={useSmallThumbs ? classes.imageInputSmall : classes.imageInput}>
                <div className={useSmallThumbs ? classes.imageInputPlaceholderSmall : classes.imageInputPlaceholder}>
                  {onCancel &&
                    <div className={classes.cancelUpload}>
                      <Button
                        kind="cancel"
                        variant="icon"
                        size="small"
                        onClick={onCancel}
                      />
                    </div>
                  }
                  <Box display='flex' flexDirection='column' alignItems='center'>
                    <Description style={{ marginBottom: theme.spacing(1) }} />
                    <Text variant="caption" color='textMuted'>
                      {`${percentageProps}%`}
                    </Text>
                    <div className={useSmallThumbs ? classes.uploadProgressSmall : classes.uploadProgress}>
                      <LinearProgress />
                    </div>
                  </Box>
                </div>
              </div>
              <div className={classes.imageListInputSpacing} />
            </>
          }
          {lightboxOpen && (
            <LightBox
              open={lightboxOpen}
              close={() => setLightboxOpen(false)}
              slides={lightboxSrc}
              carousel={{ finite: true }}
              plugins={[Zoom, Thumbnails, Download, Video]}
              zoom={{ maxZoomPixelRatio: 2 }}
              index={lightboxIdx}
            />
          )}
        </div>
      </div>
    )

  return (
    <Text color="textSecondary" variant={textVariantNoAttachment}>No attachments</Text>
  )
}

export const FileListView = withResizeDetector(FileListViewInner)

export default FileListView